import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { 
  ChevronRight, 
  Mic,
  Loader2,
  Check,
  ArrowRight,
  Keyboard
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useVoiceRecorder } from "@/hooks/useVoiceRecorder";
import { PulseLine } from "@/components/animations/PulseLine";
import { VoiceWaveform } from "@/components/MemoAI/VoiceWaveform";
import logo from "@/assets/logo.png";
import { cn } from "@/lib/utils";

type OnboardingStep = 
  | "identity"
  | "company"
  | "first-signal"
  | "complete";

interface KPITemplate {
  name: string;
  unit: "#" | "%" | "₹";
}

const KPI_PRESETS: Record<string, { label: string; emoji: string; kpis: KPITemplate[] }> = {
  saas: {
    label: "SaaS",
    emoji: "💻",
    kpis: [
      { name: "MRR", unit: "₹" },
      { name: "Churn Rate", unit: "%" },
      { name: "NPS", unit: "#" },
      { name: "CAC", unit: "₹" },
      { name: "Active Users", unit: "#" },
    ],
  },
  ecommerce: {
    label: "E-Commerce",
    emoji: "🛒",
    kpis: [
      { name: "GMV", unit: "₹" },
      { name: "AOV", unit: "₹" },
      { name: "Return Rate", unit: "%" },
      { name: "Conversion Rate", unit: "%" },
      { name: "Orders", unit: "#" },
    ],
  },
  services: {
    label: "Services",
    emoji: "🤝",
    kpis: [
      { name: "Revenue", unit: "₹" },
      { name: "Pipeline", unit: "₹" },
      { name: "Utilization", unit: "%" },
      { name: "NPS", unit: "#" },
      { name: "New Clients", unit: "#" },
    ],
  },
  custom: {
    label: "Custom",
    emoji: "✏️",
    kpis: [],
  },
};

interface OnboardingData {
  name: string;
  companyName: string;
  firstKPIName: string;
  firstKPIUnit: string;
  firstValue: number | null;
  inputPreference: "voice" | "text";
  selectedPreset: string | null;
  selectedKPIs: KPITemplate[];
}

const STEPS: OnboardingStep[] = ["identity", "company", "first-signal", "complete"];

export function OnboardingFlow() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>("identity");
  const [data, setData] = useState<OnboardingData>({
    name: "",
    companyName: "",
    firstKPIName: "",
    firstKPIUnit: "#",
    firstValue: null,
    inputPreference: "text",
    selectedPreset: null,
    selectedKPIs: [],
  });
  const [loading, setLoading] = useState(false);
  const [showTextInput, setShowTextInput] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [pulseAnimated, setPulseAnimated] = useState(false);
  const [trafficLightStep, setTrafficLightStep] = useState(0);

  const currentIndex = STEPS.indexOf(currentStep);
  const progress = ((currentIndex + 1) / STEPS.length) * 100;

  // Voice recorder for identity step
  const identityVoice = useVoiceRecorder({
    onTranscript: (text) => {
      const cleanName = text.replace(/[.!?,;]+$/, '').trim();
      setData(prev => ({ ...prev, name: cleanName, inputPreference: "voice" }));
      setTimeout(() => next(), 800);
    },
    onError: (error) => {
      toast.error(error);
      setShowTextInput(true);
    },
  });

  // Voice recorder for company step
  const companyVoice = useVoiceRecorder({
    onTranscript: async (text) => {
      setAiProcessing(true);
      await extractCompanyAndKPI(text);
      setAiProcessing(false);
    },
    onError: (error) => {
      toast.error(error);
      setShowTextInput(true);
    },
  });

  const extractCompanyAndKPI = async (text: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-router`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            input: `Extract company name and first KPI from: "${text}". Return JSON with company_name and kpi_name.`,
            channel: "onboarding",
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        const extracted = result.data || {};
        
        // Fallback: use simple parsing
        const words = text.split(/[.,;]/);
        const companyName = extracted.company_name || words[0]?.trim() || text;
        const kpiName = extracted.kpi_name || "Revenue";
        
        setData(prev => ({
          ...prev,
          companyName,
          firstKPIName: kpiName,
          firstKPIUnit: kpiName.toLowerCase().includes("revenue") || kpiName.toLowerCase().includes("mrr") ? "₹" : 
                        kpiName.toLowerCase().includes("nps") || kpiName.toLowerCase().includes("percent") ? "%" : "#",
        }));
        
        setTimeout(() => next(), 800);
      }
    } catch (error) {
      console.error("Extract error:", error);
      setData(prev => ({ ...prev, companyName: text, firstKPIName: "MRR", firstKPIUnit: "₹" }));
    }
  };

  const next = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < STEPS.length) {
      setCurrentStep(STEPS[nextIndex]);
      setShowTextInput(false);
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update profile
      if (data.name) {
        await supabase.from("profiles").update({
          name: data.name,
        }).eq("id", user.id);
      }

      // Create company if provided
      let companyId: string | null = null;
      if (data.companyName) {
        const { data: newCompany, error } = await supabase.from("companies").insert({
          name: data.companyName,
          created_by: user.id,
        }).select().single();
        
        if (error) throw error;
        companyId = newCompany.id;

        // Create KPIs from template or manual entry
        if (companyId) {
          const kpisToCreate = data.selectedKPIs.length > 0
            ? data.selectedKPIs.map((kpi, i) => ({
                company_id: companyId!,
                name: kpi.name,
                unit: kpi.unit,
                owner_user_id: user.id,
                sort_order: i,
              }))
            : data.firstKPIName
              ? [{
                  company_id: companyId,
                  name: data.firstKPIName,
                  unit: data.firstKPIUnit as "#" | "%" | "₹",
                  owner_user_id: user.id,
                  sort_order: 0,
                }]
              : [];

          if (kpisToCreate.length > 0) {
            await supabase.from("kpis").insert(kpisToCreate);
          }
        }
      }

      // Save user preferences
      await supabase.from("user_preferences").upsert({
        user_id: user.id,
        preferred_input: data.inputPreference,
        onboarding_completed: true,
        default_company_id: companyId,
      }, {
        onConflict: "user_id",
      });

      localStorage.setItem("onboardingComplete", "true");
      
      toast.success("Welcome to Memo! 🎉");
      navigate("/app");
    } catch (error) {
      console.error("Error completing onboarding:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Trigger pulse animation on first-signal step
  useEffect(() => {
    if (currentStep === "first-signal") {
      const timer = setTimeout(() => setPulseAnimated(true), 300);
      const t1 = setTimeout(() => setTrafficLightStep(1), 1200);
      const t2 = setTimeout(() => setTrafficLightStep(2), 1600);
      const t3 = setTimeout(() => setTrafficLightStep(3), 2000);
      return () => { clearTimeout(timer); clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [currentStep]);

  // Load user's name from profile if available
  useEffect(() => {
    const loadProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: profile } = await supabase.from("profiles").select("name").eq("id", user.id).single();
        if (profile?.name && profile.name !== user.email) {
          setData(prev => ({ ...prev, name: profile.name }));
        }
      }
    };
    loadProfile();
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Progress bar */}
      <div className="fixed top-0 left-0 right-0 z-50">
        <Progress value={progress} className="h-1 rounded-none" />
      </div>

      {/* Skip button */}
      <div className="fixed top-4 right-4 z-50">
        <Button
          variant="ghost"
          size="sm"
          onClick={completeOnboarding}
          disabled={loading}
          className="text-muted-foreground text-xs rounded-full"
        >
          Skip →
        </Button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          
          {/* Screen 1: Identity */}
          {currentStep === "identity" && (
            <div className="text-center space-y-8 animate-in-up">
              {/* Logo */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-accent rounded-3xl blur-2xl opacity-30 animate-float" />
                  <img 
                    src={logo} 
                    alt="Memo" 
                    className="relative h-20 w-20 rounded-3xl shadow-2xl" 
                  />
                </div>
              </div>
              
              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  I'm <span className="gradient-text">Memo</span>
                </h1>
                <p className="text-xl text-muted-foreground">
                  What should I call you?
                </p>
              </div>

              {/* Voice Input */}
              <div className="space-y-6">
                {!showTextInput ? (
                  <div className="flex flex-col items-center gap-6">
                    <button
                      onClick={identityVoice.toggleRecording}
                      disabled={identityVoice.isProcessing}
                      className={cn(
                        "relative h-24 w-24 rounded-full transition-all duration-300",
                        identityVoice.isRecording 
                          ? "bg-danger scale-110" 
                          : "bg-accent hover:scale-105 active:scale-95",
                        "flex items-center justify-center shadow-xl"
                      )}
                    >
                      {identityVoice.isProcessing ? (
                        <Loader2 className="h-10 w-10 text-white animate-spin" />
                      ) : identityVoice.isRecording ? (
                        <VoiceWaveform isActive={true} color="danger" className="h-10" />
                      ) : (
                        <Mic className="h-10 w-10 text-white" />
                      )}
                      
                      {identityVoice.isRecording && (
                        <div className="absolute inset-0 rounded-full border-4 border-danger/50 animate-ping" />
                      )}
                    </button>

                    <p className="text-sm text-muted-foreground">
                      {identityVoice.isRecording ? "Listening..." : 
                       identityVoice.isProcessing ? "Processing..." : "Tap to speak"}
                    </p>

                    <button
                      onClick={() => setShowTextInput(true)}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      <Keyboard className="h-3.5 w-3.5" />
                      Type instead
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Input
                      placeholder="Your name"
                      value={data.name}
                      onChange={(e) => setData({ ...data, name: e.target.value })}
                      className="text-lg h-14 rounded-xl text-center"
                      autoFocus
                      onKeyDown={(e) => e.key === "Enter" && data.name.trim() && next()}
                    />
                    <Button 
                      onClick={next} 
                      className="w-full rounded-xl h-12 bg-gradient-accent"
                      disabled={!data.name.trim()}
                    >
                      Continue
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {data.name && !showTextInput && (
                <div className="animate-in-up">
                  <p className="text-lg">
                    Nice to meet you, <span className="font-semibold gradient-text">{data.name}</span>!
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Screen 2: Company + First KPI */}
          {currentStep === "company" && (
            <div className="text-center space-y-8 animate-in-up">
              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  Let's <span className="gradient-text">track</span>
                </h1>
                <p className="text-lg text-muted-foreground max-w-xs mx-auto">
                  Tell me your company name and one metric you track weekly.
                </p>
                <p className="text-sm text-muted-foreground/60 italic">
                  Example: "Waypoint. We track MRR."
                </p>
              </div>

              {/* Voice Input */}
              <div className="space-y-6">
                {!showTextInput ? (
                  <div className="flex flex-col items-center gap-6">
                    <button
                      onClick={companyVoice.toggleRecording}
                      disabled={companyVoice.isProcessing || aiProcessing}
                      className={cn(
                        "relative h-24 w-24 rounded-full transition-all duration-300",
                        companyVoice.isRecording 
                          ? "bg-danger scale-110" 
                          : "bg-accent hover:scale-105 active:scale-95",
                        "flex items-center justify-center shadow-xl"
                      )}
                    >
                      {companyVoice.isProcessing || aiProcessing ? (
                        <Loader2 className="h-10 w-10 text-white animate-spin" />
                      ) : companyVoice.isRecording ? (
                        <VoiceWaveform isActive={true} color="danger" className="h-10" />
                      ) : (
                        <Mic className="h-10 w-10 text-white" />
                      )}
                      
                      {companyVoice.isRecording && (
                        <div className="absolute inset-0 rounded-full border-4 border-danger/50 animate-ping" />
                      )}
                    </button>

                    <p className="text-sm text-muted-foreground">
                      {companyVoice.isRecording ? "Listening..." : 
                       aiProcessing ? "Understanding..." : 
                       companyVoice.isProcessing ? "Processing..." : "Tap to speak"}
                    </p>

                    <button
                      onClick={() => setShowTextInput(true)}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-smooth"
                    >
                      <Keyboard className="h-3.5 w-3.5" />
                      Type instead
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Input
                      placeholder="Company name"
                      value={data.companyName}
                      onChange={(e) => setData({ ...data, companyName: e.target.value })}
                      className="text-lg h-14 rounded-xl"
                      autoFocus
                    />

                    {/* KPI Template Presets */}
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground text-left">Choose a KPI template:</p>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(KPI_PRESETS).map(([key, preset]) => (
                          <button
                            key={key}
                            onClick={() => setData(prev => ({
                              ...prev,
                              selectedPreset: key,
                              selectedKPIs: preset.kpis,
                              firstKPIName: preset.kpis[0]?.name || "",
                              firstKPIUnit: preset.kpis[0]?.unit || "#",
                            }))}
                            className={cn(
                              "p-3 rounded-xl border text-left transition-all text-sm",
                              data.selectedPreset === key
                                ? "border-primary bg-primary/5 shadow-sm"
                                : "border-border/50 hover:border-primary/30"
                            )}
                          >
                            <span className="text-lg">{preset.emoji}</span>
                            <span className="ml-2 font-medium">{preset.label}</span>
                            {preset.kpis.length > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {preset.kpis.map(k => k.name).join(", ")}
                              </p>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {data.selectedPreset === "custom" && (
                      <Input
                        placeholder="First KPI (e.g., MRR, NPS, Revenue)"
                        value={data.firstKPIName}
                        onChange={(e) => setData({ ...data, firstKPIName: e.target.value })}
                        className="h-12 rounded-xl"
                      />
                    )}

                    <Button 
                      onClick={next} 
                      className="w-full rounded-xl h-12 bg-gradient-accent"
                      disabled={!data.companyName.trim()}
                    >
                      Continue
                      <ChevronRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>

              {data.companyName && !showTextInput && (
                <div className="animate-in-up space-y-2">
                  <p className="text-lg font-semibold gradient-text">{data.companyName}</p>
                  {data.firstKPIName && (
                    <p className="text-sm text-muted-foreground">
                      Tracking: {data.firstKPIName}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Screen 3: First Signal (WOW Moment #1) */}
          {currentStep === "first-signal" && (
            <div className="text-center space-y-8 animate-in-up">
              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  Your first <span className="gradient-text">signal</span>
                </h1>
              </div>

              {/* Animated Pulse Line - WOW MOMENT */}
              <div className="relative py-8">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/5 to-transparent rounded-3xl" />
                
                {/* Company name badge */}
                <div className="mb-6 animate-in-up" style={{ animationDelay: "200ms" }}>
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted border border-border/50 text-sm font-medium shadow-sm">
                    <img src={logo} alt="Memo" className="h-5 w-5 rounded" />
                    {data.companyName || "Your Company"}
                  </span>
                </div>

                {/* Pulse Line Animation */}
                <div className="h-20 mb-6">
                  {pulseAnimated && (
                    <PulseLine 
                      color="primary" 
                      animate={true} 
                      duration={1.5}
                      height={80}
                      points={[0.3, 0.5, 0.4, 0.7, 0.5, 0.8, 0.6, 0.75, 0.7, 0.85, 0.8, 0.9]}
                    />
                  )}
                </div>

                {/* KPI Card with animated Traffic Lights */}
                <div 
                  className="bg-card border rounded-2xl p-6 shadow-lg mx-auto max-w-xs animate-in-up"
                  style={{ animationDelay: "800ms" }}
                >
                  <div className="flex items-center justify-between">
                    <div className="text-left">
                      <p className="text-sm text-muted-foreground">{data.firstKPIName || "Your first KPI"}</p>
                      <p className="text-2xl font-bold tabular-nums mt-1">—</p>
                    </div>
                    <div className="flex gap-2">
                      <div className={cn(
                        "h-3.5 w-3.5 rounded-full transition-all duration-500",
                        trafficLightStep >= 1 ? "bg-success shadow-[0_0_8px_2px_hsl(var(--success)/0.4)]" : "bg-muted"
                      )} />
                      <div className={cn(
                        "h-3.5 w-3.5 rounded-full transition-all duration-500",
                        trafficLightStep >= 2 ? "bg-warning shadow-[0_0_8px_2px_hsl(var(--warning)/0.4)]" : "bg-muted"
                      )} />
                      <div className={cn(
                        "h-3.5 w-3.5 rounded-full transition-all duration-500",
                        trafficLightStep >= 3 ? "bg-danger shadow-[0_0_8px_2px_hsl(var(--danger)/0.4)]" : "bg-muted"
                      )} />
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-muted-foreground text-sm max-w-xs mx-auto">
                Submit a value and Memo shows you{" "}
                <span className="text-success font-medium">green</span>,{" "}
                <span className="text-warning font-medium">amber</span>, or{" "}
                <span className="text-danger font-medium">red</span> — instantly.
              </p>

              <Button 
                onClick={next} 
                className="w-full rounded-xl h-12 bg-gradient-accent glow-accent"
              >
                Continue
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Screen 4: Complete */}
          {currentStep === "complete" && (
            <div className="text-center space-y-8 animate-in-up">
              {/* Success Animation */}
              <div className="flex justify-center">
                <div className="relative">
                  <div className="absolute inset-0 bg-success/20 rounded-full blur-2xl animate-pulse" />
                  <div className="relative h-24 w-24 rounded-full bg-success flex items-center justify-center shadow-xl">
                    <Check className="h-12 w-12 text-success-foreground" />
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h1 className="text-3xl font-bold tracking-tight">
                  All <span className="gradient-text">set</span>
                  {data.name && <>, {data.name}</>}
                </h1>
                <p className="text-lg text-muted-foreground max-w-xs mx-auto">
                  Your weekly Memo arrives <span className="font-medium text-foreground">Sunday 11 AM</span>.
                </p>
              </div>

              <div className="bg-card border border-border/50 rounded-2xl p-6 space-y-4 text-left shadow-sm">
                <p className="text-sm text-muted-foreground text-center font-medium">
                  Just talk to me anytime to:
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {[
                    "Submit values",
                    "Check status",
                    "Generate reports",
                    "Get insights",
                    "Compare periods",
                    "Portfolio overview"
                  ].map((item, i) => (
                    <div key={item} className="flex items-center gap-2 animate-in-up" style={{ animationDelay: `${i * 80}ms` }}>
                      <Check className="h-4 w-4 text-success flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <Button 
                onClick={completeOnboarding}
                disabled={loading}
                className="w-full rounded-xl h-14 text-lg bg-gradient-accent glow-accent"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Setting up...
                  </>
                ) : (
                  <>
                    Start using Memo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
